export interface WorkspaceCheckoutReturn {
  workspaceId: string
  checkoutType: "create" | "resubscribe"
  payment: "success" | "cancel"
}

export const isBillingPathname = (pathname: string): boolean =>
  /^\/billing(?:\/(?:success|credits-success))?\/?$/.test(pathname) ||
  /^\/team-billing\/[^/?#]+\/?$/.test(pathname)

export const isPersonalBillingReturnPath = (pathname: string) =>
  /^\/billing\/(?:success|credits-success)\/?$/.test(pathname)

export const getPendingWorkspaceCheckoutReturn = (
  pathname: string,
  search: string,
  storage?: Pick<Storage, "getItem">,
): WorkspaceCheckoutReturn | null => {
  if (
    pathname !== "/" &&
    !/^\/billing\/?$/.test(pathname) &&
    !/^\/team-billing\/[^/]+\/?$/.test(pathname)
  ) {
    return null
  }

  const params = new URLSearchParams(search)
  if (params.has("creditPackSuccess") || params.has("creditPackCancel")) {
    return null
  }

  let workspaceId: string | null
  let checkoutType: string | null
  try {
    const checkoutStorage = storage ?? sessionStorage
    workspaceId = checkoutStorage.getItem("pendingWorkspaceId")
    checkoutType = checkoutStorage.getItem("pendingWorkspaceCheckoutType")
  } catch {
    return null
  }
  if (
    !workspaceId ||
    (checkoutType !== null &&
      checkoutType !== "create" &&
      checkoutType !== "resubscribe")
  ) {
    return null
  }

  const payment = params.has("session_id") ? "success" : params.get("payment")
  if (payment !== "success" && payment !== "cancel") return null

  return { workspaceId, checkoutType: checkoutType ?? "create", payment }
}

export const withoutWorkspaceCheckoutParams = (search: string) => {
  const params = new URLSearchParams(search)
  params.delete("session_id")
  params.delete("payment")
  const nextSearch = params.toString()
  return nextSearch ? `?${nextSearch}` : ""
}

export const getBillingContextPath = (
  workspaceId: string | null,
  search = "",
  hash = "",
) =>
  `${workspaceId ? `/team-billing/${workspaceId}` : "/billing"}${search}${hash}`

export const getPersonalBillingRedirectPath = (
  workspaceId: string | null,
  pathname: string,
  search: string,
  hash: string,
) => {
  if (!workspaceId || isPersonalBillingReturnPath(pathname)) {
    return null
  }

  return getBillingContextPath(workspaceId, search, hash)
}

export const getWorkspaceCreationReturnPaths = (
  pathname: string,
  isMobileOrTablet: boolean,
) => {
  const returnPath =
    isMobileOrTablet && isBillingPathname(pathname) ? "/billing" : "/"
  return { successPath: returnPath, cancelPath: `${returnPath}?payment=cancel` }
}
